import React, { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { BuildingOfficeIcon, PlusIcon, FunnelIcon, DocumentTextIcon, ChevronDownIcon, ChevronUpIcon, DocumentIcon, ClockIcon, ClipboardDocumentListIcon, CogIcon } from '@heroicons/react/24/outline';
import { Menu } from '@headlessui/react';
import { businessPermitsAPI } from '../../services/api/admin/businessPermitApi';
import StatisticsCard from '../../components/common/StatisticsCard';
import ChartsSection from '../../components/businessPermit/ChartsSection';
import BusinessProfilesTable from '../../components/businessPermit/BusinessProfilesTable';
import BusinessRegistrationModal from '../../components/businessPermit/BusinessRegistrationModal';
import ViewBusinessModal from '../../components/businessPermit/ViewBusinessModal';
import BusinessPermitApplicationsModal from '../../components/businessPermit/BusinessPermitApplicationsModal';
import RenewalExpiryMonitoringModal from '../../components/businessPermit/RenewalExpiryMonitoringModal';
import PermitRequirementsModal from '../../components/businessPermit/PermitRequirementsModal';
import Collapsible from '../../components/common/Collapsible';
import SearchBar from '../../components/common/SearchBar';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import Button from '../../components/common/Button';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import { showSuccess, showError } from '../../utils/notificationHelper';
import { CheckCircleIcon } from 'lucide-react';

const buildBusinessStats = (statsData) => {
  const data = statsData?.data?.data ?? statsData?.data ?? statsData ?? {};
  return [
    {
      label: 'Total Registered Businesses',
      value: data.total_registered_business ?? "",
      bgColor: 'bg-indigo-100',
      textColor: 'text-indigo-600',
      description: 'Total businesses on record'
    },
    {
      label: 'New Applications',
      value: data.total_applications_this_year ?? "",
      bgColor: 'bg-yellow-100',
      textColor: 'text-yellow-600',
      description: 'Applications filed this year'
    },
    {
      label: 'Pending Endorsements',
      value: data.total_pending_endorsements ?? "",
      bgColor: 'bg-orange-100',
      textColor: 'text-orange-600',
      description: 'Applications awaiting endorsement'
    },
    {
      label: 'Approved Permits',
      value: data.total_approved_permits ?? "",
      bgColor: 'bg-green-100',
      textColor: 'text-green-600',
      description: 'Permits issued and approved'
    },
    {
      label: 'Expired Permits',
      value: data.total_expired_permits ?? "",
      bgColor: 'bg-red-100',
      textColor: 'text-red-600',
      description: 'Permits past validity period'
    },
    {
      label: 'For Renewal',
      value: data.total_permits_for_renewal ?? "",
      bgColor: 'bg-blue-100',
      textColor: 'text-blue-600',
      description: 'Permits expiring within 30/60 Days'
    }
  ];
};

const BusinessPermitsPage = () => {
  const queryClient = useQueryClient();
  const [selectedBusiness, setSelectedBusiness] = useState(null);
  const [showRegistrationModal, setShowRegistrationModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [businessToDelete, setBusinessToDelete] = useState(null);
  const [showApplicationsModal, setShowApplicationsModal] = useState(false);
  const [showRenewalExpiryModal, setShowRenewalExpiryModal] = useState(false);
  const [showPermitRequirementsModal, setShowPermitRequirementsModal] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    business_type: '',
    classification: '',
    search: '',
    startDate: null,
    endDate: null,
    is_resident: '',
  });
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [searchValue, setSearchValue] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    lastPage: 1,
    perPage: 10,
    total: 0,
  });

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: searchValue }));
      setPagination(prev => ({ ...prev, currentPage: 1 }));
    }, 500);

    return () => clearTimeout(timer);
  }, [searchValue]);


  // Fetch business profiles using React Query
  const {
    data: businessProfilesData,
    isLoading: businessProfilesLoading,
    error: businessProfilesError,
    refetch: refetchBusinessProfiles
  } = useQuery(
    ['businessPermits', filters, pagination.currentPage, pagination.perPage],
    () => {
      const params = {
        page: pagination.currentPage,
        per_page: pagination.perPage,
        business_type: filters.business_type || undefined,
        classification: filters.classification || undefined,
        from: filters.startDate || undefined,
        is_resident: filters.is_resident || undefined,
        search: filters.search || undefined,
        status: filters.status || undefined,
        to: filters.endDate || undefined,
      };

      // Remove undefined values
      Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

      return businessPermitsAPI.getAll(params);
    },
    {
      staleTime: 2 * 60 * 1000,
      cacheTime: 5 * 60 * 1000,
      keepPreviousData: true,
      onSuccess: (response) => {
        const businessProfiles = response.data?.data?.businesses || [];

        // Extract pagination info from meta
        const paginationData = response.data?.data?.meta || {};

        setPagination(prev => ({
          ...prev,
          total: paginationData.total || businessProfiles.length,
          lastPage: paginationData.last_page || Math.max(1, Math.ceil(businessProfiles.length / prev.perPage)),
          currentPage: paginationData.current_page || prev.currentPage,
        }));
      },
      onError: (error) => {
        console.error('Error fetching business profiles:', error);
        showError('Failed to load business profiles data');
      },
    }
  );

  // Extract business profiles from response
  const businessProfiles = React.useMemo(() => {
    if (!businessProfilesData) return [];
    const businesses = businessProfilesData.data?.data?.businesses || [];
    return businesses;
  }, [businessProfilesData]);

  // Fetch statistics using React Query - similar to AyudaPage pattern
  const { data: statsData, isLoading: statisticsLoading, error: statisticsError } = useQuery(
    'businessPermitsStatistics',
    businessPermitsAPI.getStatistics,
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      onError: (error) => {
        console.error('Error fetching business permits statistics:', error);
        showError('Failed to load statistics data');
      },
    }
  );

  // Build statistics using the helper function
  const businessStats = buildBusinessStats(statsData);

  // Create business mutation
  const createBusinessMutation = useMutation(
    (data) => businessPermitsAPI.create(data),
    {
      onSuccess: () => {
        showSuccess('Business registered successfully');
        setShowRegistrationModal(false);
        setSelectedBusiness(null);
        // Invalidate queries to refresh data
        queryClient.invalidateQueries(['businessPermits']);
        queryClient.invalidateQueries(['businessPermitsStatistics']);
      },
      onError: (error) => {
        console.error('Error creating business:', error);
        showError(error.response?.data?.message || 'Failed to create business profile');
      },
    }
  );

  // Update business mutation
  const updateBusinessMutation = useMutation(
    ({ id, data }) => businessPermitsAPI.update(id, data),
    {
      onSuccess: () => {
        showSuccess('Business profile updated successfully');
        setShowRegistrationModal(false);
        setSelectedBusiness(null);
        // Invalidate queries to refresh data
        queryClient.invalidateQueries(['businessPermits']);
        queryClient.invalidateQueries(['businessPermitsStatistics']);
      },
      onError: (error) => {
        console.error('Error updating business:', error);
        showError(error.response?.data?.message || 'Failed to update business profile');
      },
    }
  );

  // Delete business mutation
  const deleteBusinessMutation = useMutation(
    (id) => businessPermitsAPI.delete(id),
    {
      onSuccess: () => {
        showSuccess('Business profile deleted successfully');
        setShowDeleteModal(false);
        setBusinessToDelete(null);
        // Invalidate queries to refresh data
        queryClient.invalidateQueries(['businessPermits']);
        queryClient.invalidateQueries(['businessPermitsStatistics']);
      },
      onError: (error) => {
        console.error('Error deleting business:', error);
        showError(error.response?.data?.message || 'Failed to delete business profile');
      },
    }
  );

  // Combined loading state for form submission
  const isSubmitting = createBusinessMutation.isLoading || updateBusinessMutation.isLoading;



  const handleView = async (business) => {
    // Fetch full business details using API
    if (business.id) {
      try {
        const response = await businessPermitsAPI.getById(business.id);
        setSelectedBusiness(response.data?.data || response.data || business);
      } catch (error) {
        console.error('Error fetching business details:', error);
        // Fallback to passed business data if API fails
        setSelectedBusiness(business);
      }
    } else {
      setSelectedBusiness(business);
    }
    setShowViewModal(true);
  };

  const handleEdit = async (business) => {
    if (business?.id) {
      try {
        const response = await businessPermitsAPI.getById(business.id);
        setSelectedBusiness(response.data?.data || response.data || business);
      } catch (error) {
        console.error('Error fetching business details for edit:', error);
        setSelectedBusiness(business);
      }
    } else {
      setSelectedBusiness(business);
    }

    setShowRegistrationModal(true);
    setShowViewModal(false);
  };

  const handleDelete = (business) => {
    setBusinessToDelete(business);
    setShowDeleteModal(true);
  };

  const confirmDelete = () => {
    if (businessToDelete) {
      deleteBusinessMutation.mutate(businessToDelete.id);
    }
  };

  const handleRegistrationSubmit = (data) => {
    if (selectedBusiness) {
      updateBusinessMutation.mutate({ id: selectedBusiness.id, data });
    } else {
      createBusinessMutation.mutate(data);
    }
  };

  const handlePageChange = (page, perPage) => {
    setPagination(prev => ({
      ...prev,
      currentPage: page,
      perPage: perPage || prev.perPage,
    }));
  };

  const handlePerPageChange = (perPage) => {
    setPagination(prev => ({
      ...prev,
      perPage,
      currentPage: 1,
    }));
  };

  const handleExport = async () => {
  try {
    const params = {
      classification: filters.classification || undefined,
      business_type: filters.business_type || undefined,
      status: filters.status || undefined,
      search: filters.search || undefined,
      from: filters.startDate || undefined,
      to: filters.endDate || undefined,
      is_resident: filters.is_resident || undefined,
    };

    // Remove undefined values
    Object.keys(params).forEach(key => params[key] === undefined && delete params[key]);

    const response = await businessPermitsAPI.export(params);

    // response.data is the blob directly when responseType: 'blob' is set
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;

    const filename = filters.classification
      ? `business-permits-${filters.classification}.csv`
      : 'business-permits.csv';

    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);

    showSuccess('Business permits exported successfully');
  } catch (error) {
    console.error('Error exporting business permits:', error);
    showError('Failed to export business permits');
  }
};


  // Use businessProfiles directly since API handles filtering
  const filteredProfiles = businessProfiles;
  
  return (
    <div className="max-w-9xl mx-auto p-2">
      {/* Header Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-4">
            <div className="bg-blue-50 p-3 rounded-lg">
              <BuildingOfficeIcon className="h-8 w-8 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-gray-900 mb-2">
                Business Registration and Permit Management
              </h1>
              <p className="text-gray-600">
                Centralize business permit records, processing status, and compliance information.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Menu as="div" className="relative inline-block text-left">
              {({ open }) => (
                <>
                  <Menu.Button className="flex items-center justify-between gap-2 px-4 py-2 bg-indigo-100 text-indigo-700 rounded-md text-sm font-semibold min-w-[200px]">
                    <div className="flex items-center gap-2">
                      <BuildingOfficeIcon className="w-5 h-5 text-indigo-700" />
                      Business Permits
                    </div>
                    {open ? (
                      <ChevronUpIcon className="w-4 h-4 text-indigo-700" />
                    ) : (
                      <ChevronDownIcon className="w-4 h-4 text-indigo-700" />
                    )}
                  </Menu.Button>

                  <Menu.Items className="absolute right-0 mt-2 w-80 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black/5 focus:outline-none z-20">
                    <div className="py-1">
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            className={`flex items-center gap-3 w-full px-4 py-3 text-sm ${active
                              ? 'bg-indigo-50 text-indigo-700'
                              : 'text-gray-700'
                              }`}
                            onClick={() => {
                              setShowApplicationsModal(true);
                            }}
                          >
                            <DocumentIcon className="h-5 w-5 text-indigo-600" />
                            <span>Business Permits</span>
                          </button>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            className={`flex items-center gap-3 w-full px-4 py-3 text-sm ${active
                              ? 'bg-indigo-50 text-indigo-700'
                              : 'text-gray-700'
                              }`}
                            onClick={() => {
                              setShowRenewalExpiryModal(true);
                            }}
                          >
                            <ClockIcon className="h-5 w-5 text-orange-600" />
                            <span>Renewal/Expiry Monitoring</span>
                          </button>
                        )}
                      </Menu.Item>
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            className={`flex items-center gap-3 w-full px-4 py-3 text-sm ${active
                              ? 'bg-indigo-50 text-indigo-700'
                              : 'text-gray-700'
                              }`}
                            onClick={() => {
                              setShowPermitRequirementsModal(true);
                            }}
                          >
                            <CheckCircleIcon className="h-5 w-5 text-purple-600" />
                            <span>Business Permit Requirements</span>
                          </button>
                        )}
                      </Menu.Item>
                    </div>
                  </Menu.Items>
                </>
              )}
            </Menu>
            <Button
              variant="primary"
              size="md"
              icon={<PlusIcon className="h-5 w-5" />}
              onClick={() => {
                setSelectedBusiness(null);
                setShowRegistrationModal(true);
              }}
            >
              New Registration
            </Button>
          </div>
        </div>
      </div>
      {statisticsLoading ? (
        <div className="grid grid-cols-1 sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-4 mb-8">
          {[...Array(6)].map((_, index) => (
            <div key={index} className="bg-white rounded-lg shadow-lg p-4 animate-pulse">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className="flex justify-between items-center gap-2">
                    <div className="w-12 h-12 bg-gray-200 rounded-lg"></div>
                    <div className="flex flex-col justify-center">
                      <div className="h-4 bg-gray-200 rounded w-24 mb-1"></div>
                      <div className="h-3 bg-gray-200 rounded w-32"></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : statisticsError ? (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8">
          <p className="text-red-600 text-center">Failed to load statistics data</p>
        </div>
      ) : (
        <StatisticsCard stats={businessStats} />
      )}

      {/* Collapsible Charts Section */}
      <div className="mb-8">
        <Collapsible
          title="Summary of Business Registration and Permit Activities"
          description="Summary of business permit applications, endorsements, and permit status."
          icon={
            <div className="bg-blue-50 p-2 rounded-lg">
              <DocumentTextIcon className="h-6 w-6 text-blue-600" />
            </div>
          }
          defaultOpen={false}
        >
          <ChartsSection
            barangayData={statsData?.data?.data?.summary?.business_per_barangay || statsData?.data?.summary?.business_per_barangay || []}
            businessTypeData={statsData?.data?.data?.summary?.business_type_distribution || statsData?.data?.summary?.business_type_distribution || {}}
            permitApplicationsData={statsData?.data?.data?.summary?.monthly_applications || statsData?.data?.summary?.monthly_applications || {}}
          />
        </Collapsible>
      </div>

      {/* Business Profiles Section */}
      <BusinessProfilesTable
        data={filteredProfiles}
        onView={handleView}
        onEdit={handleEdit}
        onDelete={handleDelete}
        pagination={pagination}
        onPageChange={(page, perPage) => handlePageChange(page, perPage)}
        isLoading={businessProfilesLoading}
        header={
          <div>
            {/* Search & Filter */}
            <div className="flex justify-between items-center pb-3">
              <h2 className="font-semibold text-gray-800 flex items-center gap-2">
                {filterOpen ? (
                  <>
                    <FunnelIcon className="h-5 w-5 text-blue-600" />
                    <span>Filter Business Profiles</span>
                  </>
                ) : (
                  "Business Profiles"
                )}
              </h2>
              <div className="flex items-center gap-2">
                <div className="relative w-[24rem]">
                  <SearchBar
                    value={searchValue}
                    onChange={(e) => {
                      setSearchValue(e.target.value);
                    }}
                    placeholder="Search businesses by name, type, status..."
                  />
                </div>

                {/* Filter Button */}
                <Button
                  variant="outline"
                  size="sm"
                  icon={<FunnelIcon className="h-4 w-4" />}
                  onClick={() => setFilterOpen((prev) => !prev)}
                >
                  Filter
                </Button>

                {/* Export Button */}
                <Button
                  variant="outline"
                  size="sm"
                  icon={<ArrowDownTrayIcon className="h-4 w-4" />}
                  onClick={handleExport}
                >
                  Export
                </Button>
              </div>
            </div>

            {/* Filter dropdowns */}
            {filterOpen && (
              <div className="pb-3 flex justify-center gap-8 text-sm">
                {/* Date Range Filter */}
                <div className="flex flex-col flex-1 relative">
                  <label className="text-sm font-semibold text-gray-800 mb-1">
                    Date
                  </label>
                  <div className="flex gap-2">
                    <div className="relative flex-1">
                      <input
                        type="date"
                        value={startDate || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setStartDate(value);
                          setFilters(prev => ({ ...prev, startDate: value }));
                          setPagination(prev => ({ ...prev, currentPage: 1 }));
                        }}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm 
                             focus:border-blue-500 focus:ring-blue-500"
                        placeholder="dd/mm/yyyy"
                      />
                    </div>
                    <div className="relative flex-1">
                      <input
                        type="date"
                        value={endDate || ''}
                        onChange={(e) => {
                          const value = e.target.value;
                          setEndDate(value);
                          setFilters(prev => ({ ...prev, endDate: value }));
                          setPagination(prev => ({ ...prev, currentPage: 1 }));
                        }}
                        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm 
                             focus:border-blue-500 focus:ring-blue-500"
                        placeholder="dd/mm/yyyy"
                      />
                    </div>
                  </div>
                </div>

                {/* Business Type Select */}
                <div className="flex flex-col flex-1 relative">
                  <label className="text-sm font-semibold text-gray-800 mb-1">
                    Business Type
                  </label>
                  <div className="relative">
                    <select
                      value={filters.business_type || "All Business Types"}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFilters(prev => ({ ...prev, business_type: value === "All Business Types" ? "" : value }));
                        setPagination(prev => ({ ...prev, currentPage: 1 }));
                        setOpenDropdown(null);
                      }}
                      onClick={() =>
                        setOpenDropdown((prev) =>
                          prev === "business_type" ? null : "business_type",
                        )
                      }
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm 
                           focus:border-blue-500 focus:ring-blue-500 appearance-none pr-8"
                    >
                      <option>All Business Types</option>
                      <option value="retail">Retail</option>
                      <option value="service">Service</option>
                      <option value="manufacturing">Manufacturing</option>
                      <option value="others">Others</option>
                    </select>
                    {openDropdown === "business_type" ? (
                      <ChevronUpIcon className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    ) : (
                      <ChevronDownIcon className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    )}
                  </div>
                </div>

                {/* Status Select */}
                <div className="flex flex-col flex-1 relative">
                  <label className="text-sm font-semibold text-gray-800 mb-1">
                    Permit Status
                  </label>
                  <div className="relative">
                    <select
                      value={filters.status || "All Status"}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFilters(prev => ({ ...prev, status: value === "All Status" ? "" : value }));
                        setPagination(prev => ({ ...prev, currentPage: 1 }));
                        setOpenDropdown(null);
                      }}
                      onClick={() =>
                        setOpenDropdown((prev) =>
                          prev === "status" ? null : "status",
                        )
                      }
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm 
                           focus:border-blue-500 focus:ring-blue-500 appearance-none pr-8"
                    >
                      <option>All Status</option>
                      <option value="active">Active</option>
                      <option value="closed">Closed</option>
                      <option value="suspended">Suspended</option>
                      <option value="pending">Pending</option>
                      <option value="approved">Approved</option>
                    </select>
                    {openDropdown === "status" ? (
                      <ChevronUpIcon className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    ) : (
                      <ChevronDownIcon className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    )}
                  </div>
                </div>

                {/* Classification Select */}
                <div className="flex flex-col flex-1 relative">
                  <label className="text-sm font-semibold text-gray-800 mb-1">
                    Application Type
                  </label>
                  <div className="relative">
                    <select
                      value={filters.classification || "All Types"}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFilters(prev => ({ ...prev, classification: value === "All Types" ? "" : value }));
                        setPagination(prev => ({ ...prev, currentPage: 1 }));
                        setOpenDropdown(null);
                      }}
                      onClick={() =>
                        setOpenDropdown((prev) =>
                          prev === "classification" ? null : "classification",
                        )
                      }
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm 
                           focus:border-blue-500 focus:ring-blue-500 appearance-none pr-8"
                    >
                      <option>All Types</option>
                      <option value="micro">Micro</option>
                      <option value="small">Small</option>
                      <option value="medium">Medium</option>
                      <option value="large">Large</option>
                    </select>
                    {openDropdown === "classification" ? (
                      <ChevronUpIcon className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    ) : (
                      <ChevronDownIcon className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    )}
                  </div>
                </div>

                {/* Is Resident Select */}
                <div className="flex flex-col flex-1 relative">
                  <label className="text-sm font-semibold text-gray-800 mb-1">
                    Owner is Resident?
                  </label>
                  <div className="relative">
                    <select
                      value={filters.is_resident || "All"}
                      onChange={(e) => {
                        const value = e.target.value;
                        setFilters(prev => ({ ...prev, is_resident: value === "All" ? "" : value }));
                        setPagination(prev => ({ ...prev, currentPage: 1 }));
                        setOpenDropdown(null);
                      }}
                      onClick={() =>
                        setOpenDropdown((prev) =>
                          prev === "is_resident" ? null : "is_resident",
                        )
                      }
                      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm 
                           focus:border-blue-500 focus:ring-blue-500 appearance-none pr-8"
                    >
                      <option>All</option>
                      <option value="true">Yes</option>
                      <option value="false">No</option>
                    </select>
                    {openDropdown === "is_resident" ? (
                      <ChevronUpIcon className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    ) : (
                      <ChevronDownIcon className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        }
      />

      {/* Modals */}
      <BusinessRegistrationModal
        isOpen={showRegistrationModal}
        onClose={() => {
          setShowRegistrationModal(false);
          setSelectedBusiness(null);
        }}
        initialData={selectedBusiness}
        onSubmit={handleRegistrationSubmit}
        isLoading={isSubmitting}
      />

      <ViewBusinessModal
        isOpen={showViewModal}
        onClose={() => {
          setShowViewModal(false);
          setSelectedBusiness(null);
        }}
        business={selectedBusiness}
        onEdit={handleEdit}
      />

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setBusinessToDelete(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Business Profile"
        message={`Are you sure you want to delete "${businessToDelete?.business_name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        confirmButtonClass="bg-red-600 hover:bg-red-700 text-white"
        isLoading={deleteBusinessMutation.isLoading}
      />

      <BusinessPermitApplicationsModal
        isOpen={showApplicationsModal}
        onClose={() => setShowApplicationsModal(false)}
      />

      <RenewalExpiryMonitoringModal
        isOpen={showRenewalExpiryModal}
        onClose={() => setShowRenewalExpiryModal(false)}
      />

      <PermitRequirementsModal
        isOpen={showPermitRequirementsModal}
        onClose={() => setShowPermitRequirementsModal(false)}
      />
    </div>
  );
};

export default BusinessPermitsPage;
